/**
 * @packageDocumentation
 * @module f1/abstract-data-type
 */

import { t_rec_arr, t_key, t_rec, t_arr } from '../types';
import fold from './fold';
import mimic from '../fn/mimic';

type t_map_fn<V, W> = (v: V, k: t_key, adt: t_rec_arr<V>) => W;

export interface Map {
  /**
   * Alter the values of an ADT
   *
   * @public
   * @function f1.map
   * @param fn map function to transform all values in the sequence
   * @param adt the input to alter
   * @example
   *
   * ```ts
   * f1.map((v) => v + 1, [1, 2]) === [2, 3]
   * ```
   */
  <V, W>(fn: t_map_fn<V, W>, adt: t_arr<V>): t_arr<W>;
  <V, W>(fn: t_map_fn<V, W>, adt: t_rec<string, V>): t_rec<string, W>;
  <V, W>(fn: t_map_fn<V, W>, adt: t_rec_arr<V>): t_rec_arr<W>;

  <V, W>(fn: t_map_fn<V, W>): (adt: t_arr<V>) => t_arr<W>;
  <V, W>(fn: t_map_fn<V, W>): (adt: t_rec<string, V>) => t_rec<string, W>;
  <V, W>(fn: t_map_fn<V, W>): (adt: t_rec_arr<V>) => t_rec_arr<W>;
}

/**
 * @see Map
 */
export default <V, W>(fn: t_map_fn<V, W>, adt: t_rec_arr<V>): t_rec_arr<W> => {
  return fold<V, t_rec_arr<V>, t_rec_arr<W>>(
    (acc, v, k, adt) => {
      acc[k] = fn(v, k, adt);
      return acc;
    },
    mimic((adt as unknown) as t_rec_arr<V>),
    adt
  );
};
